/* Russo Music menu drawer (sections/rm-menu-drawer.liquid). Net-new, not stock Concept.
   Opening and closing the drawer is the theme's own <drawer-element>. This file only adds:
   - the Shop / Services / Stores tabs
   - sliding panels (category → sub-list, Help), with Back
   - click tracking through Shopify customer events ("rm_menu_click")
   - loading the category panels (Section Rendering API) as soon as someone reaches for the menu
   Links that open a panel are real links. If a tile is tapped while its panel is still loading, the tap
   waits for it (up to PANEL_WAIT_MS); if the panel never arrives, it goes to the link. */

if (!customElements.get('rm-menu')) {
  const PANEL_WAIT_MS = 2500;

  class RmMenu extends HTMLElement {
    connectedCallback() {
      // The theme moves an open drawer to <body>, which reconnects this element. Set up once.
      if (this.initialized) return;
      this.initialized = true;

      this.stack = [];
      this.drawer = this.closest('drawer-element');
      this.trackingEnabled = this.dataset.tracking === 'true';

      (this.drawer || this).addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.drawer?.addEventListener('drawer:afterHide', () => this.reset());

      // Start fetching panels on the first sign of intent: a touch, hover or focus on any menu button,
      // or the drawer opening some other way (theme editor, mobile dock).
      const load = () => this.loadPanels();
      document.querySelectorAll('[aria-controls="MenuDrawer"]').forEach((control) => {
        ['pointerdown', 'mouseenter', 'focus'].forEach((type) => control.addEventListener(type, load, { once: true, passive: true }));
      });
      this.drawer?.addEventListener('toggle', load);

      this.enhanceOpeners();
    }

    // Links that open a panel are announced as buttons once their panel is in the page, since they no
    // longer go to a new page. Until then they stay plain links, which is what they do.
    enhanceOpeners() {
      this.querySelectorAll('a[data-rm-open]').forEach((opener) => {
        const screen = this.findScreen(opener);
        if (!screen) return;
        opener.setAttribute('role', 'button');
        opener.setAttribute('aria-controls', screen.id);
      });
    }

    announce(message) {
      const status = this.querySelector('[data-rm-status]');
      if (status) status.textContent = message || '';
    }

    get panels() {
      return this.querySelector('.rm-menu__panels');
    }

    loadPanels() {
      if (this.panelsPromise) return this.panelsPromise;

      const container = this.panels;
      if (!container || container.hasAttribute('loaded') || !this.dataset.sectionId) {
        this.panelsPromise = Promise.resolve();
        return this.panelsPromise;
      }

      const root = window.theme?.routes?.root_url || '/';
      const url = `${root}${root.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(this.dataset.sectionId)}`;

      this.panelsPromise = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Menu panels: HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(container.id);
          if (fresh && this.panels === container) {
            container.replaceWith(fresh);
            this.enhanceOpeners();
          }
        })
        .catch((error) => {
          console.error(error);
          this.panelsPromise = null; // try again next time; meanwhile tiles work as plain links
        });

      return this.panelsPromise;
    }

    get tabs() {
      return Array.from(this.querySelectorAll('[data-rm-tab]'));
    }

    findScreen(opener) {
      const screen = document.getElementById(opener.dataset.rmOpen);
      return screen && this.contains(screen) ? screen : null;
    }

    get activeScreen() {
      return this.stack.length ? this.stack[this.stack.length - 1].screen : this.querySelector('[data-rm-tabpanel].is-active');
    }

    onClick(event) {
      const tab = event.target.closest('[data-rm-tab]');
      if (tab) {
        this.selectTab(tab.dataset.rmTab);
        this.track(tab, 'tab');
        return;
      }

      // A click with detail 0 came from the keyboard (Enter/Space), not a tap or mouse click.
      const fromKeyboard = event.detail === 0;

      if (event.target.closest('[data-rm-back]')) {
        event.preventDefault();
        this.back(fromKeyboard);
        return;
      }

      const opener = event.target.closest('[data-rm-open]');
      if (opener) {
        const screen = this.findScreen(opener);
        if (screen) {
          event.preventDefault();
          this.track(opener, 'open');
          this.open(screen, opener, fromKeyboard);
          return;
        }

        // Panel still on its way: hold the tap instead of leaving the page.
        const pending = this.loadPanels();
        if (pending && this.panels && !this.panels.hasAttribute('loaded')) {
          event.preventDefault();
          if (opener.getAttribute('aria-busy') === 'true') return;

          opener.setAttribute('aria-busy', 'true');
          this.announce(this.dataset.loadingText);
          Promise.race([pending, new Promise((resolve) => setTimeout(resolve, PANEL_WAIT_MS))]).then(() => {
            opener.removeAttribute('aria-busy');
            this.announce('');
            if (this.drawer && !this.drawer.open) return; // closed while waiting

            const arrived = this.findScreen(opener);
            if (arrived) {
              this.track(opener, 'open');
              this.open(arrived, opener, fromKeyboard);
            } else if (opener.href) {
              this.track(opener, 'click');
              window.location.href = opener.href;
            }
          });
          return;
        }
      }

      const link = event.target.closest('a[href]');
      if (link) this.track(link, 'click');

      // The search bar opens the theme's search drawer; close this one behind it.
      if (event.target.closest('[data-rm-close]')) this.drawer?.hide();
    }

    onKeydown(event) {
      // Links given role="button" must also activate with Space, like a real button.
      const opener = event.target.closest('a[role="button"][data-rm-open]');
      if (opener && (event.key === ' ' || event.key === 'Spacebar')) {
        event.preventDefault();
        opener.click();
        return;
      }

      const tab = event.target.closest('[data-rm-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const tabs = this.tabs;
      let index = tabs.indexOf(tab);
      if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = tabs.length - 1;

      event.preventDefault();
      this.selectTab(tabs[index].dataset.rmTab);
      tabs[index].focus();
    }

    selectTab(name) {
      this.clearStack();

      this.tabs.forEach((tab) => {
        const selected = tab.dataset.rmTab === name;
        tab.setAttribute('aria-selected', selected);
        tab.tabIndex = selected ? 0 : -1;
      });

      this.querySelectorAll('[data-rm-tabpanel]').forEach((panel) => {
        const active = panel.dataset.rmTabpanel === name;
        panel.classList.toggle('is-active', active);
        panel.classList.remove('is-behind');
        if (active) panel.scrollTop = 0;
      });
    }

    open(screen, opener, fromKeyboard = false) {
      const from = this.activeScreen;
      if (!from || from === screen) return;

      screen.scrollTop = 0;
      from.classList.remove('is-active');
      from.classList.add('is-behind');
      screen.classList.add('is-active');
      this.stack.push({ screen, from, opener });

      // Keyboard users land on Back, with the theme's focus outline. After a tap, focus goes to the
      // panel itself (no outline, see .rm-menu__screen:focus) so screen readers still move with it
      // but no blue ring appears under the title.
      const back = screen.querySelector('[data-rm-back]');
      setTimeout(() => this.moveFocus(fromKeyboard && back ? back : screen), 50);
    }

    back(fromKeyboard = false) {
      const entry = this.stack.pop();
      if (!entry) return;

      entry.screen.classList.remove('is-active');
      entry.from.classList.remove('is-behind');
      entry.from.classList.add('is-active');

      this.moveFocus(fromKeyboard && entry.opener ? entry.opener : entry.from);
    }

    moveFocus(element) {
      if (element.hasAttribute('data-rm-screen')) {
        element.tabIndex = -1;
        // Name the panel after its heading so a screen reader announces where focus landed.
        const label = element.querySelector('h2')?.textContent;
        if (label && !element.hasAttribute('role')) {
          element.setAttribute('role', 'group');
          element.setAttribute('aria-label', label.replace(/\s+/g, ' ').trim());
        }
      }
      element.focus({ preventScroll: true });
    }

    clearStack() {
      this.stack.forEach(({ screen, from }) => {
        screen.classList.remove('is-active', 'is-behind');
        from.classList.remove('is-behind');
      });
      this.stack = [];
    }

    reset() {
      if (this.tabs.length) {
        this.selectTab(this.tabs[0].dataset.rmTab);
      } else {
        this.clearStack();
        this.querySelector('[data-rm-tabpanel]')?.classList.add('is-active');
      }
    }

    track(element, action) {
      if (!this.trackingEnabled) return;

      const detail = {
        action,
        area: element.closest('[data-rm-area]')?.dataset.rmArea || 'menu',
        label: (element.dataset.rmLabel || element.textContent).replace(/\s+/g, ' ').trim(),
        url: element.getAttribute('href') || '',
        page: window.location.pathname
      };

      try {
        window.Shopify?.analytics?.publish?.('rm_menu_click', detail);
      } catch (error) {
        // Tracking must never block navigation.
      }
      document.dispatchEvent(new CustomEvent('rm:menu-click', { detail }));
    }
  }

  customElements.define('rm-menu', RmMenu);
}
